import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDropdownSourceContext, useRtl } from "@ryde/redux";
import {
  ActionsEnum,
  IDropdownItem,
  IInjectedDrawerProps,
  IPriceRateSearchCurrentRateInfo,
  IPriceRateSearchLogsDrawerProps,
  IPriceRateSearchMatchedSummary,
  IPriceRateSearchRideContext,
  IRichLogData,
  isValidPositiveNumber,
  Log,
  PriceRateSearchExplainMode,
  RidesApi,
  useHasPermissions,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { LayoutRightLoader } from "../../../../right";
import { CommonDefaultDrawer } from "../../common-default-drawer";
import { BaseDrawer, FormFooter } from "../../drawer";
import {
  IRichLogsFilter,
  RichLogsFilters,
  RichLogsFormWrapper,
  RichLogsPagination,
  RichViewLogItem,
} from "../rich-logs-components";
import { useRichLogsContent } from "../rich-logs-components/hooks/use-rich-logs.content";
import { RichLogsHeader } from "../rich-logs-components/rich-logs.header";
import { PriceRateSearchLogsToolbar } from "./price-rate-search-logs-toolbar";
import { PriceRateSearchLogsDrawerWrapperStyled } from "./price-rate-search-logs.styled";
import {
  buildStubPriceRateSearchCurrentRate,
  PRICE_RATE_SEARCH_LOGS_REFRESH_DEBOUNCE_MS,
  resolveInitialPriceRateSearchLock,
  resolvePriceRateSearchResetTargets,
  resolvePriceRateSearchTitleFromDropdown,
} from "./price-rate-search-logs.utils";

const PRICE_RATE_SEARCH_LOGS_FORM_DEFAULTS: Pick<
  IRichLogsFilter,
  "withAiHintsOnly"
> = {
  withAiHintsOnly: false,
};

const PriceRateSearchLogsContent: FC<{
  logData: IRichLogData | undefined;
  onRefreshLogsClick: () => void;
  isRefreshLogsDisabled: boolean;
}> = ({ logData, onRefreshLogsClick, isRefreshLogsDisabled }) => {
  const { t } = useTranslation();
  const {
    searchText,
    currentPage,
    totalPages,
    startIndex,
    paginatedLogs,
    handleLogItemClick,
  } = useRichLogsContent({ logData });

  return (
    <>
      {logData && (
        <RichLogsFilters
          logData={logData}
          hasExternalLogs={Boolean(logData)}
          isFinished
          isRefreshLogsDisabled={isRefreshLogsDisabled}
          defaultFiltersCollapsed
          isStatsCollapsed={false}
          onRefreshLogsClick={onRefreshLogsClick}
          withAiHintsOnlyToggle
          hideGroupField
          collapsedPanelLabel={t("ride.priceRateSearchLogFilters", {
            defaultValue: "Log filters",
          })}
          sectionEmoji="📋"
        />
      )}
      <RichLogsPagination totalPages={totalPages} currentPage={currentPage} />
      {paginatedLogs.length ? (
        paginatedLogs.map((record, index) => (
          <RichViewLogItem
            key={`${record.timestamp}-${startIndex + index}`}
            record={record}
            onItemClick={handleLogItemClick}
            searchText={searchText}
            idOnlyLinks
          />
        ))
      ) : (
        <p className="log__no-logs-found">
          {t("rideProduction.formLabel.noLogsFound")}
        </p>
      )}
      <RichLogsPagination totalPages={totalPages} currentPage={currentPage} />
    </>
  );
};

export const PriceRateSearchLogsDrawer: FC<
  IPriceRateSearchLogsDrawerProps & IInjectedDrawerProps
> = ({
  drawerNumber,
  onCloseDrawer,
  rideId,
  supplierId: supplierIdFromRide,
  vehicleTypeId: vehicleTypeIdFromRide,
  priceRateId: priceRateIdFromRide,
}) => {
  const { t } = useTranslation();
  const { isRtl } = useRtl();
  const { getDropdownItems, getDropdownLabel } = useDropdownSourceContext();
  const { hasPermissionViewPrices } = useHaveCustomPermissions();
  const { hasPermissions: hasSupplierReadPermission } = useHasPermissions({
    entity: "supplier",
    action: ActionsEnum.Read,
  });
  const [logData, setLogData] = useState<IRichLogData | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [explainMode, setExplainMode] = useState(
    PriceRateSearchExplainMode.Save,
  );
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
    supplierIdFromRide ?? null,
  );
  const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState<
    number | null
  >(vehicleTypeIdFromRide ?? null);
  const [lockCurrentPriceRate, setLockCurrentPriceRate] = useState(false);
  const [lockSupplier, setLockSupplier] = useState(
    resolveInitialPriceRateSearchLock(supplierIdFromRide),
  );
  const [lockVehicleType, setLockVehicleType] = useState(
    resolveInitialPriceRateSearchLock(vehicleTypeIdFromRide),
  );
  const [supplierOptions, setSupplierOptions] = useState<IDropdownItem[]>([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<IDropdownItem[]>(
    [],
  );
  const [currentPriceRate, setCurrentPriceRate] =
    useState<IPriceRateSearchCurrentRateInfo | null>(
      buildStubPriceRateSearchCurrentRate(priceRateIdFromRide),
    );
  const [matchedPriceRate, setMatchedPriceRate] =
    useState<IPriceRateSearchMatchedSummary | null>(null);
  const [matchedPriceRateId, setMatchedPriceRateId] = useState<number | null>(
    null,
  );
  const [rideSearchContext, setRideSearchContext] =
    useState<IPriceRateSearchRideContext | null>(null);
  const hasInitializedSelectionFromRide = useRef(
    isValidPositiveNumber(supplierIdFromRide) ||
      isValidPositiveNumber(vehicleTypeIdFromRide),
  );
  const hasInitializedLocksFromRide = useRef(
    supplierIdFromRide !== undefined || vehicleTypeIdFromRide !== undefined,
  );
  const filteredVehicleTypeOptions = useMemo(() => {
    if (!lockSupplier || selectedSupplierId === null) {
      return vehicleTypeOptions;
    }
    const matching = vehicleTypeOptions.filter(
      item => !item.supplierId || item.supplierId === selectedSupplierId,
    );
    return matching.length ? matching : vehicleTypeOptions;
  }, [lockSupplier, selectedSupplierId, vehicleTypeOptions]);

  const fetchPriceRateSearchDetails = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      try {
        const result = await RidesApi.getPriceRateSearchDetails(rideId, {
          explainMode,
          ...(explainMode === PriceRateSearchExplainMode.Produce
            ? {}
            : {
                lockSupplier,
                lockVehicleType,
                ...(lockSupplier ? { supplierId: selectedSupplierId } : {}),
                ...(lockVehicleType
                  ? { vehicleTypeId: selectedVehicleTypeId }
                  : {}),
                lockCurrentPriceRateIfExists: lockCurrentPriceRate,
              }),
        });
        if (isCancelled()) {
          return;
        }
        if (result?.success && result.data?.logs) {
          setLogData({ records: result.data.logs });
        } else {
          setLogData({ records: [] });
        }
        setMatchedPriceRate(result?.data?.matchedPriceRate ?? null);
        setMatchedPriceRateId(
          isValidPositiveNumber(result?.data?.matchedPriceRateId)
            ? result.data.matchedPriceRateId
            : null,
        );
        const rideContext = result?.data?.rideContext;
        if (rideContext) {
          setRideSearchContext(rideContext);
          setCurrentPriceRate(rideContext.currentPriceRate ?? null);
          if (!hasInitializedSelectionFromRide.current) {
            hasInitializedSelectionFromRide.current = true;
            setSelectedSupplierId(rideContext.supplierId);
            setSelectedVehicleTypeId(rideContext.vehicleTypeId);
          }
          if (!hasInitializedLocksFromRide.current) {
            hasInitializedLocksFromRide.current = true;
            setLockSupplier(
              resolveInitialPriceRateSearchLock(rideContext.supplierId),
            );
            setLockVehicleType(
              resolveInitialPriceRateSearchLock(rideContext.vehicleTypeId),
            );
          }
        }
      } catch (e) {
        Log.error("getPriceRateSearchDetails failed", e);
        if (!isCancelled()) {
          setLogData({ records: [] });
          setMatchedPriceRate(null);
          setMatchedPriceRateId(null);
        }
      } finally {
        if (!isCancelled()) {
          setLoading(false);
        }
      }
    },
    [
      rideId,
      explainMode,
      lockSupplier,
      lockVehicleType,
      selectedSupplierId,
      selectedVehicleTypeId,
      lockCurrentPriceRate,
    ],
  );

  const hasLoadedPriceRateSearchOnce = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const runFetch = (): void => {
      void fetchPriceRateSearchDetails(() => cancelled);
    };

    if (!hasLoadedPriceRateSearchOnce.current) {
      hasLoadedPriceRateSearchOnce.current = true;
      runFetch();
      return () => {
        cancelled = true;
      };
    }

    const timeoutId = setTimeout(
      runFetch,
      PRICE_RATE_SEARCH_LOGS_REFRESH_DEBOUNCE_MS,
    );
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [fetchPriceRateSearchDetails]);

  useEffect(() => {
    let cancelled = false;
    const loadDropdowns = async (): Promise<void> => {
      const [suppliers, vehicleTypes] = await Promise.all([
        hasSupplierReadPermission
          ? getDropdownItems?.("supplier")
          : Promise.resolve([] as IDropdownItem[]),
        getDropdownItems?.("vehicleType"),
      ]);
      if (cancelled) {
        return;
      }
      setSupplierOptions(suppliers ?? []);
      setVehicleTypeOptions(vehicleTypes ?? []);
    };
    void loadDropdowns();
    return () => {
      cancelled = true;
    };
  }, [getDropdownItems, hasSupplierReadPermission]);

  useEffect(() => {
    let cancelled = false;
    const loadPriceRateTitle = async (): Promise<void> => {
      if (!currentPriceRate?.id || currentPriceRate.label?.trim()) {
        return;
      }
      const dropdownLabel = await getDropdownLabel?.(
        "priceRate",
        currentPriceRate.id,
      );
      if (cancelled || !dropdownLabel?.trim()) {
        return;
      }
      const label = resolvePriceRateSearchTitleFromDropdown(
        dropdownLabel,
        currentPriceRate.id,
      );
      if (!label) {
        return;
      }
      setCurrentPriceRate(previous =>
        previous?.id === currentPriceRate.id && !previous.label?.trim()
          ? { ...previous, label }
          : previous,
      );
    };
    void loadPriceRateTitle();
    return () => {
      cancelled = true;
    };
  }, [currentPriceRate?.id, currentPriceRate?.label, getDropdownLabel]);

  const handleExplainModeChange = useCallback(
    (mode: PriceRateSearchExplainMode) => {
      setExplainMode(mode);
      if (mode === PriceRateSearchExplainMode.Produce) {
        setLockCurrentPriceRate(false);
      }
    },
    [],
  );

  const handleSupplierIdChange = useCallback(
    (nextSupplierId: number | null) => {
      setSelectedSupplierId(nextSupplierId);
    },
    [],
  );

  const handleVehicleTypeIdChange = useCallback(
    (nextVehicleTypeId: number | null) => {
      setSelectedVehicleTypeId(nextVehicleTypeId);
    },
    [],
  );

  const handleRefreshPriceRateSearchLogs = useCallback(() => {
    void fetchPriceRateSearchDetails(() => false);
  }, [fetchPriceRateSearchDetails]);

  const handleResetToCurrent = useCallback(() => {
    const { supplierId, vehicleTypeId } = resolvePriceRateSearchResetTargets({
      currentPriceRate,
      rideSupplierId: rideSearchContext?.supplierId,
      rideVehicleTypeId: rideSearchContext?.vehicleTypeId,
    });
    if (isValidPositiveNumber(supplierId)) {
      setSelectedSupplierId(supplierId);
      setLockSupplier(true);
    }
    if (isValidPositiveNumber(vehicleTypeId)) {
      setSelectedVehicleTypeId(vehicleTypeId);
      setLockVehicleType(true);
    }
  }, [
    currentPriceRate,
    rideSearchContext?.supplierId,
    rideSearchContext?.vehicleTypeId,
  ]);

  const title = t("ride.priceRateSearchLogsTitle", {
    defaultValue: "Price rate search details",
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <RichLogsFormWrapper
        logData={logData}
        defaultValues={PRICE_RATE_SEARCH_LOGS_FORM_DEFAULTS}
      >
        <CommonDefaultDrawer
          onCloseDrawer={onCloseDrawer}
          Header={
            <RichLogsHeader
              onCloseDrawer={onCloseDrawer}
              entity="ride"
              isFinished
              logsFormWatched={logData ?? { records: [] }}
              logsFromWatchedApiOld={[]}
              title={title}
              idOnlyLinks
            />
          }
          Footer={
            <FormFooter
              cancelLabel={t("general.CLOSE")}
              onCancelClick={onCloseDrawer}
            />
          }
        >
          <PriceRateSearchLogsDrawerWrapperStyled $isRtl={isRtl}>
            <PriceRateSearchLogsToolbar
              currentPriceRate={currentPriceRate}
              explainMode={explainMode}
              isRefreshLogsDisabled={loading}
              isSearchSettled={logData !== undefined}
              matchedPriceRate={matchedPriceRate}
              matchedPriceRateId={matchedPriceRateId}
              onRefreshLogsClick={handleRefreshPriceRateSearchLogs}
              rideSearchContext={rideSearchContext}
              hasSupplierReadPermission={hasSupplierReadPermission}
              lockCurrentPriceRate={lockCurrentPriceRate}
              lockSupplier={lockSupplier}
              lockVehicleType={lockVehicleType}
              onExplainModeChange={handleExplainModeChange}
              onLockCurrentPriceRateChange={setLockCurrentPriceRate}
              onLockSupplierChange={setLockSupplier}
              onLockVehicleTypeChange={setLockVehicleType}
              onSupplierIdChange={handleSupplierIdChange}
              onResetToCurrent={handleResetToCurrent}
              onVehicleTypeIdChange={handleVehicleTypeIdChange}
              showMinPrice={hasPermissionViewPrices}
              supplierId={selectedSupplierId}
              supplierOptions={supplierOptions}
              vehicleTypeId={selectedVehicleTypeId}
              vehicleTypeOptions={filteredVehicleTypeOptions}
            />
            {loading && logData === undefined ? (
              <LayoutRightLoader />
            ) : (
              <PriceRateSearchLogsContent
                isRefreshLogsDisabled={loading}
                logData={logData}
                onRefreshLogsClick={handleRefreshPriceRateSearchLogs}
              />
            )}
          </PriceRateSearchLogsDrawerWrapperStyled>
        </CommonDefaultDrawer>
      </RichLogsFormWrapper>
    </BaseDrawer>
  );
};
